import { sanitizeBusinessMessage, toUserFacingApiError } from '../utils/userFacingError'
import { api } from './api'

function assertSuccess(body: unknown) {
  const b = body as { success?: boolean; message?: string; data?: unknown }
  if (!b?.success) {
    throw new Error(sanitizeBusinessMessage(b?.message, 'Request failed'))
  }
  return b.data
}

function apiError(err: unknown, fallback: string) {
  return toUserFacingApiError(err, fallback)
}

export type SpecialOfferListItem = {
  id: number
  storeId: number
  slug: string
  title: string
  badge: string | null
  subtitle: string
  heroImage: string | null
  discountType: 'percent' | 'fixed'
  discountValue: number
  startsAt: string
  endsAt: string
  sortOrder: number
  createdAt: string
  updatedAt: string
  explicitProductCount: number
  categoryRuleCount: number
}

export type SpecialOfferExplicitProduct = {
  id: number
  name: string
  slug: string
}

export type SpecialOfferDetail = Omit<SpecialOfferListItem, 'explicitProductCount' | 'categoryRuleCount'> & {
  productIds: number[]
  /** Present on GET detail from current API; same ids as productIds. */
  explicitProducts?: SpecialOfferExplicitProduct[]
  storeCategoryIds: number[]
}

export async function listSpecialOffers(storeId: number): Promise<{ items: SpecialOfferListItem[] }> {
  try {
    const { data } = await api.get(`/storebusiness/stores/${encodeURIComponent(storeId)}/special-offers`)
    return assertSuccess(data) as { items: SpecialOfferListItem[] }
  } catch (e) {
    throw apiError(e, 'Failed to load special offers')
  }
}

export async function getSpecialOffer(storeId: number, offerId: number): Promise<SpecialOfferDetail> {
  try {
    const { data } = await api.get(
      `/storebusiness/stores/${encodeURIComponent(storeId)}/special-offers/${encodeURIComponent(offerId)}`,
    )
    return assertSuccess(data) as SpecialOfferDetail
  } catch (e) {
    throw apiError(e, 'Failed to load special offer')
  }
}

export async function createSpecialOffer(
  storeId: number,
  payload: {
    title: string
    slug?: string
    badge?: string | null
    subtitle?: string
    heroImage?: string | null
    discountType: 'percent' | 'fixed'
    discountValue: number
    startsAt: string
    endsAt: string
    sortOrder?: number
  },
): Promise<SpecialOfferListItem> {
  try {
    const { data } = await api.post(
      `/storebusiness/stores/${encodeURIComponent(storeId)}/special-offers`,
      payload,
    )
    return assertSuccess(data) as SpecialOfferListItem
  } catch (e) {
    throw apiError(e, 'Failed to create special offer')
  }
}

export async function updateSpecialOffer(
  storeId: number,
  offerId: number,
  patch: Partial<{
    title: string
    slug: string
    badge: string | null
    subtitle: string
    heroImage: string | null
    discountType: 'percent' | 'fixed'
    discountValue: number
    startsAt: string
    endsAt: string
    sortOrder: number
  }>,
): Promise<SpecialOfferListItem> {
  try {
    const { data } = await api.patch(
      `/storebusiness/stores/${encodeURIComponent(storeId)}/special-offers/${encodeURIComponent(offerId)}`,
      patch,
    )
    return assertSuccess(data) as SpecialOfferListItem
  } catch (e) {
    throw apiError(e, 'Failed to update special offer')
  }
}

export async function deleteSpecialOffer(storeId: number, offerId: number): Promise<void> {
  try {
    const { data } = await api.delete(
      `/storebusiness/stores/${encodeURIComponent(storeId)}/special-offers/${encodeURIComponent(offerId)}`,
    )
    assertSuccess(data)
  } catch (e) {
    throw apiError(e, 'Failed to delete special offer')
  }
}

export async function addOfferProducts(storeId: number, offerId: number, productIds: number[]): Promise<void> {
  try {
    const { data } = await api.post(
      `/storebusiness/stores/${encodeURIComponent(storeId)}/special-offers/${encodeURIComponent(offerId)}/products`,
      { productIds },
    )
    assertSuccess(data)
  } catch (e) {
    throw apiError(e, 'Failed to add products to offer')
  }
}

export async function removeOfferProduct(storeId: number, offerId: number, productId: number): Promise<void> {
  try {
    const { data } = await api.delete(
      `/storebusiness/stores/${encodeURIComponent(storeId)}/special-offers/${encodeURIComponent(offerId)}/products/${encodeURIComponent(productId)}`,
    )
    assertSuccess(data)
  } catch (e) {
    throw apiError(e, 'Failed to remove product from offer')
  }
}

export async function addOfferCategory(
  storeId: number,
  offerId: number,
  storeCategoryId: number,
): Promise<void> {
  try {
    const { data } = await api.post(
      `/storebusiness/stores/${encodeURIComponent(storeId)}/special-offers/${encodeURIComponent(offerId)}/categories`,
      { storeCategoryId },
    )
    assertSuccess(data)
  } catch (e) {
    throw apiError(e, 'Failed to add category rule')
  }
}

export async function removeOfferCategory(
  storeId: number,
  offerId: number,
  categoryId: number,
): Promise<void> {
  try {
    const { data } = await api.delete(
      `/storebusiness/stores/${encodeURIComponent(storeId)}/special-offers/${encodeURIComponent(offerId)}/categories/${encodeURIComponent(categoryId)}`,
    )
    assertSuccess(data)
  } catch (e) {
    throw apiError(e, 'Failed to remove category rule')
  }
}
